import { AppModal, Button, Label, Textarea } from "@/components/ui";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export type MatterPhaseStatusCommand =
  | {
      kind: "set";
      scope: "phase" | "action";
      id: string;
      title: string;
      status: "Blocked" | "Closed";
      stateVersion: number;
    }
  | {
      kind: "clear";
      scope: "phase" | "action";
      id: string;
      title: string;
      stateVersion: number;
    };

export function MatterPhaseStatusModal({
  command,
  onOpenChange,
  onConfirm,
  isPending,
}: {
  command?: MatterPhaseStatusCommand;
  onOpenChange: (open: boolean) => void;
  onConfirm: (text: string) => Promise<void>;
  isPending: boolean;
}) {
  const [text, setText] = useState("");

  useEffect(() => {
    if (command) setText("");
  }, [command]);

  if (!command) return null;
  const isClear = command.kind === "clear";
  const action = isClear
    ? "Clear override"
    : command.status === "Blocked"
      ? "Block"
      : "Close";

  const submit = async () => {
    const normalized = text.trim();
    if (!isClear && !normalized) {
      toast.error("A reason is required.");
      return;
    }
    await onConfirm(normalized);
  };

  return (
    <AppModal
      open
      onOpenChange={onOpenChange}
      title={`${action} ${command.title}?`}
      description={
        isClear
          ? "This removes the current manual exception and requests fresh evaluation where required."
          : command.status === "Closed"
            ? "Closed work stops applying evaluation results until reopened."
            : "Blocked work remains evaluated while the manual exception is visible."
      }
      size="lg"
      loading={isPending}
      isDirty={Boolean(text)}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant={
              command.kind === "set" && command.status === "Closed"
                ? "destructive"
                : "default"
            }
            onClick={() => void submit()}
          >
            {action}
          </Button>
        </div>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="matter-phase-status-note">
          {isClear ? "Audit note (optional)" : "Reason"}
        </Label>
        <Textarea
          id="matter-phase-status-note"
          value={text}
          maxLength={500}
          rows={4}
          disabled={isPending}
          onChange={(event) => {
            setText(event.target.value);
          }}
          placeholder={
            isClear
              ? "Explain why the exception is being cleared"
              : "Explain why this work needs an exception"
          }
        />
      </div>
    </AppModal>
  );
}
